import type { DocumentData, OrderByDirection, Query } from 'firebase/firestore'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { db } from '@/app/config/firebase'

interface UseFirestoreCollectionResult<T = DocumentData> {
  data: T[]
  loading: boolean
  error: Error | null
}

interface UseFirestoreCollectionOptions {
  orderByField?: string
  orderByDirection?: OrderByDirection
}

export function useFirestoreCollection<T = DocumentData>(
  path: string,
  options?: UseFirestoreCollectionOptions
): UseFirestoreCollectionResult<T> {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!path) {
      setLoading(false)
      return undefined
    }

    setLoading(true)
    setError(null)

    try {
      const collectionRef = collection(db, path)
      let collectionQuery: Query = collectionRef

      // Apply ordering if specified
      if (options?.orderByField) {
        collectionQuery = query(
          collectionRef,
          orderBy(options.orderByField, options.orderByDirection || 'asc')
        )
      }

      const unsubscribe = onSnapshot(
        collectionQuery,
        snapshot => {
          const documents = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as T[]

          setData(documents)
          setError(null)
          setLoading(false)
        },
        err => {
          console.error(`❌ Firestore Error [${path}]:`, err)
          setError(err as Error)
          setLoading(false)
        }
      )

      return () => {
        unsubscribe()
      }
    } catch (err) {
      console.error(`❌ Firestore Error [${path}]:`, err)
      setError(err as Error)
      setLoading(false)
      return undefined
    }
  }, [path, options?.orderByField, options?.orderByDirection])

  return { data, loading, error }
}
